import React, { useEffect, useMemo, useState } from 'react';
import { WxLink } from '@watkinslabs/watkinsx-components';
import { ARCHES as ARCH_HUE } from './palette';

// The download section. Media are published to the public host by the images
// repo's publish play; this reads the manifest that play stages beside them,
// so a new compose appears here the moment it is published and nothing has to
// be rebuilt or redeployed to announce it.
//
// Names and layout are the contract in the images repo, docs/media-naming.md:
//   releases/<release>/<Edition>/<arch>/iso/Oxide-<Ed>-Live-<rel>-<compose>.<arch>.iso
// The media host is a plain file listing, not a WatkinsX shell site, so the
// browse links open in a new tab (downloads themselves stay in place).
const HOST = 'https://oxide-pub.watkinsx.com/dist';

// One paragraph per edition, and a shape drawn for it below. Ordered smallest
// first, which is also the order someone deciding should read them in.
const EDITIONS = [
  {
    id: 'Nano',
    tag: 'Console',
    blurb: 'A text login and the tools to look around: systemd, bash, coreutils, iproute, htop. No X, no browser. The smallest thing that is still a system.',
    detail: ['systemd + networkd', 'DHCP and DNS', 'console login'],
  },
  {
    id: 'Micro',
    tag: 'Desktop',
    blurb: 'A graphical session that boots to a window manager: Xorg, JWM, a terminal, a file manager, and Firefox. Small enough to be an appliance, complete enough to browse the web on.',
    detail: ['Xorg + JWM', 'Firefox', 'systemd + networkd'],
  },
  {
    id: 'Lite',
    tag: 'Workstation',
    blurb: 'The fuller system: NetworkManager, sshd, a real toolset and the packages a session actually reaches for. This is the one to try if you want to use it rather than look at it.',
    detail: ['NetworkManager', 'sshd', 'full toolset'],
  },
];

const ARCHES = [
  { id: 'x86_64',  label: 'x86_64',  note: 'Intel and AMD' },
  { id: 'aarch64', label: 'aarch64', note: 'ARM64' },
];

// Guess once, from what the browser will admit to. A wrong guess costs one
// click and the other architecture is never hidden -- but showing an ARM image
// to a laptop is noise, and every visitor pays for it.
function guessArch() {
  if (typeof navigator === 'undefined') return 'x86_64';
  const hint = `${navigator.userAgentData?.platform ?? ''} ${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase();
  if (/arm|aarch64/.test(hint)) return 'aarch64';
  // Apple silicon reports as Intel through the legacy fields, so it lands on
  // x86_64 here. That is the honest default: it is what the string says.
  return 'x86_64';
}

const gib = (bytes) => `${(bytes / 1024 / 1024).toFixed(0)} MB`;

// ---------------------------------------------------------------------------
// A mark per edition, sized by what the edition actually contains. Same rule
// as the capability figures: geometry that comes from the thing, not clip art.
function Mark({ id }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.2 };
  if (id === 'Nano') {
    // One frame, one prompt line. Nothing else is in the image.
    return (
      <svg viewBox="0 0 48 48" className="ox-dl__mark" aria-hidden="true">
        <rect x="6" y="10" width="36" height="28" rx="2.5" {...common} />
        <path d="M12 20 l5 4 -5 4" {...common} />
        <path d="M21 28 h9" {...common} />
      </svg>
    );
  }
  if (id === 'Micro') {
    // A window manager: one frame holding smaller frames.
    return (
      <svg viewBox="0 0 48 48" className="ox-dl__mark" aria-hidden="true">
        <rect x="4" y="8" width="40" height="32" rx="2.5" {...common} />
        <path d="M4 15 h40" {...common} />
        <rect x="9" y="20" width="17" height="14" rx="1.5" {...common} />
        <rect x="29" y="20" width="10" height="9" rx="1.5" {...common} opacity="0.55" />
      </svg>
    );
  }
  // Lite: the same session with a network and a way in.
  return (
    <svg viewBox="0 0 48 48" className="ox-dl__mark" aria-hidden="true">
      <rect x="4" y="8" width="40" height="32" rx="2.5" {...common} />
      <path d="M4 15 h40" {...common} />
      <circle cx="24" cy="27" r="7" {...common} opacity="0.75" />
      <path d="M17 27 h14 M24 20 c3.5 4 3.5 10 0 14 M24 20 c-3.5 4 -3.5 10 0 14" {...common} opacity="0.75" />
    </svg>
  );
}

export default function Downloads() {
  const [manifest, setManifest] = useState(null);
  const [failed, setFailed] = useState(false);
  const [arch, setArch] = useState(guessArch);

  useEffect(() => {
    let live = true;
    fetch(`${HOST}/manifest.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => { if (live) setManifest(data); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  // Newest compose wins when several are published, which is what a visitor
  // wants; the older ones stay reachable in the directory listing.
  const byEdition = useMemo(() => {
    const out = {};
    for (const f of manifest?.files ?? []) {
      if (f.arch !== arch) continue;
      const cur = out[f.edition];
      if (!cur || f.compose > cur.compose) out[f.edition] = f;
    }
    return out;
  }, [manifest, arch]);

  const release = manifest?.files?.[0]?.release;

  return (
    // The section takes the hue of the architecture being shown (the same
    // x86_64 / aarch64 colours as the card's rails and the dashboard's parity
    // chart), so switching arch visibly switches everything below the toggle.
    <section className="ox-section ox-dl" id="download" data-reveal style={{ '--dl-arch': ARCH_HUE[arch] }}>
      <div className="ox-wrap">
        <span className="ox-kicker">Download</span>
        <h2 className="ox-h2">Run it on your own machine</h2>
        <p className="ox-dl__lede">
          Each edition is one bootable ISO: GRUB for both firmwares, the kernel, and an
          immutable root that keeps every write in memory. Write it to a USB stick with{' '}
          <code>dd</code>, or point a VM at it. Nothing is installed and nothing on the
          machine is touched.
        </p>

        <div className="ox-dl__arch" role="group" aria-label="Architecture">
          {ARCHES.map((a) => (
            <button key={a.id} type="button"
                    className={`ox-dl__archbtn${a.id === arch ? ' is-on' : ''}`}
                    style={{ '--arch': ARCH_HUE[a.id] }}
                    aria-pressed={a.id === arch}
                    onClick={() => setArch(a.id)}>
              <em><i aria-hidden="true" />{a.label}</em><span>{a.note}</span>
            </button>
          ))}
        </div>

        <div className="ox-dl__grid">
          {EDITIONS.map((ed) => {
            const file = byEdition[ed.id];
            return (
              <article className={`ox-dl__card${file ? '' : ' is-empty'}`} key={ed.id}>
                <header className="ox-dl__head">
                  <Mark id={ed.id} />
                  <div>
                    <h3 className="ox-dl__title">{ed.id}</h3>
                    <span className="ox-dl__tag">{ed.tag}</span>
                  </div>
                </header>
                <p className="ox-dl__blurb">{ed.blurb}</p>
                <ul className="ox-dl__detail">
                  {ed.detail.map((d) => <li key={d}>{d}</li>)}
                </ul>
                {file ? (
                  <>
                    <a className="ox-btn ox-btn--primary ox-dl__get"
                       href={`${HOST}/${file.path}`}>
                      <i className="fa-solid fa-download" /> {gib(file.bytes)} · {arch}
                    </a>
                    {/* The checksum is the point of publishing one. Show it, do
                        not make a visitor hunt the directory for it. */}
                    <code className="ox-dl__sum" title={file.sha256}>
                      sha256 {file.sha256.slice(0, 16)}…
                    </code>
                  </>
                ) : (
                  <span className="ox-dl__pending">
                    {failed ? 'Media host unreachable' : manifest ? `No ${arch} build published` : 'Loading…'}
                  </span>
                )}
              </article>
            );
          })}
        </div>

        <p className="ox-dl__foot">
          {manifest ? (
            <>Release {release}, compose {manifest.compose}. Every image is checksummed;{' '}
              <WxLink href={`${HOST}/releases/`} external>browse every build</WxLink> for older composes and
              the <code>CHECKSUM</code> files.</>
          ) : failed ? (
            <>The media host is not answering. Images are published at{' '}
              <WxLink href={`${HOST}/releases/`} external>{HOST.replace('https://', '')}</WxLink>.</>
          ) : null}
        </p>
      </div>
    </section>
  );
}
